import React, { useState } from "react";

import WorkspaceIcon from "@uui/Building07";
import EyeOnIcon from "@uui/Eye";
import EyeOffIcon from "@uui/EyeOff";
import InfoCircleIcon from "@uui/InfoCircle";
import PlusCircleIcon from "@uui/PlusCircle";
import { groupBy, isEmpty, partition, sortBy } from "lodash/fp";

import { Divider, Typography } from "@mui/material";
import styled from "@mui/material/styles/styled";

import Setting from "@app/analytics/account/components/Setting";
import SettingsModule from "@app/analytics/account/components/SettingsModule";
import type { SettingModule } from "@app/analytics/account/types";
import Tag from "@app/analytics/components/Tag";
import AddCustomTagPopover from "@app/analytics/components/TagPopover/AddCustomTagPopover";
import EditCustomTagPopover from "@app/analytics/components/TagPopover/EditCustomTagPopover";
import useAuth from "@app/common/auth/useAuth";
import useTags from "@app/common/hooks/tags/useTags";
import { showToast } from "@app/common/hooks/useToasts";
import type { CreateCustomTagBody, Tag as ITag } from "@app/common/types";

import Button from "@rui/Button";
import ConfirmationModal from "@rui/ConfirmationModal";
import Stack, { Column, Row } from "@rui/Stack";
import { Bold } from "@rui/styles";
import TooltipWrapper from "@rui/TooltipWrapper";

const TagList = styled(Row)({
  rowGap: "6px",
  columnGap: "6px",
  flexWrap: "wrap",
});

const TagListWrapper = styled(Column)(({ theme }) => ({
  borderRadius: "4px",
  padding: theme.spacing(1),
  rowGap: "12px",
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
  width: "100%",
}));

const TaggingModule = (props: SettingModule) => {
  const { user } = useAuth();
  const { tags, createTags, deleteTag, updateTag } = useTags();
  const [tagToConfirmDelete, setTagToConfirmDelete] = useState<ITag | null>(
    null
  );

  const groupedTags = groupBy("kind", tags);

  const handleCreateTags = (newTags: CreateCustomTagBody[]) => {
    createTags.mutate(newTags, {
      onError: () =>
        showToast({
          severity: "error",
          message:
            "Oops! Something went wrong and some tags could not be saved.",
        }),
    });
  };

  const handleUpdateTag = (updatedTag: ITag) => {
    updateTag.mutate(updatedTag, {
      onError: () =>
        showToast({
          severity: "error",
          message:
            "Oops! Something went wrong and your tag could not be updated.",
        }),
    });
  };

  const confirmDeleteTag = (tag: ITag) => {
    setTagToConfirmDelete(tag);
  };

  const handleHideTag = (tag: ITag) => {
    handleUpdateTag({
      ...tag,
      isHidden: !tag.isHidden,
    });
  };

  const resetTagToDelete = () => setTagToConfirmDelete(null);

  const handleConfirmDelete = () => {
    if (!tagToConfirmDelete) return null;

    deleteTag.mutate(tagToConfirmDelete.id, {
      onError: () =>
        showToast({
          severity: "error",
          message:
            "Oops! Something went wrong and your tag could not be deleted.",
        }),
      onSettled: resetTagToDelete,
    });
  };

  const sortedCustomTags = sortBy("name", groupedTags.custom ?? []);
  const sortedAutoTags = sortBy("name", groupedTags.auto ?? []);
  const [hiddenAutoTags, autoTags] = partition("isHidden", sortedAutoTags);

  return (
    <>
      <SettingsModule {...props}>
        <Setting
          title="Custom Tags"
          subtitle={`Create and delete custom tags. Deleting tags will remove them from any meetings to which they are applied.${
            user?.workspaceId
              ? " Workspace-managed tags must deleted by a Workspace admin."
              : null
          }`}
        >
          <Stack spacing="13px" sx={{ paddingTop: "5px" }}>
            {!isEmpty(sortedCustomTags) && (
              <TagListWrapper>
                <TagList>
                  {sortedCustomTags.map((tag) =>
                    tag.workspaceId ? (
                      <TooltipWrapper
                        title="Workspace tags must be managed by a Workspace admin in the Workspace settings."
                        key={tag.id}
                        enterDelay={1000}
                      >
                        <Tag
                          tag={tag}
                          sizing="padded"
                          startIcon={WorkspaceIcon}
                          noTitle
                        />
                      </TooltipWrapper>
                    ) : (
                      <EditCustomTagPopover
                        key={tag.id}
                        allTags={tags}
                        onSave={handleUpdateTag}
                        renderControl={(props) => (
                          <Tag
                            {...props}
                            sizing="padded"
                            onAction={
                              props.tag.workspaceId
                                ? undefined
                                : confirmDeleteTag
                            }
                          />
                        )}
                        tag={tag}
                      />
                    )
                  )}
                </TagList>
              </TagListWrapper>
            )}
            <Row>
              <AddCustomTagPopover
                allTags={tags}
                onSave={handleCreateTags}
                renderControl={(props) => (
                  <Button
                    {...props}
                    sx={{ borderRadius: "6px", height: "40px" }}
                    startIcon={<PlusCircleIcon sx={{ width: "16px" }} />}
                  >
                    Add new
                  </Button>
                )}
              />
            </Row>
          </Stack>
        </Setting>
        <Divider />
        <Setting
          title="Read Auto-Tags"
          subtitle="Read automatically assigns these tags your meetings based on the content."
        >
          <Row sx={{ paddingTop: "5px" }}>
            <TagListWrapper>
              <TagList>
                {autoTags.map((tag) => (
                  <Tag
                    sizing="padded"
                    key={tag.id}
                    tag={tag}
                    onAction={handleHideTag}
                    actionIcon={<EyeOnIcon />}
                  />
                ))}
              </TagList>
              {!isEmpty(hiddenAutoTags) && (
                <Column spacing="6px">
                  <TooltipWrapper
                    title="These tags will be hidden from your meeting reports."
                    enterDelay={500}
                  >
                    <Row
                      variant="left"
                      spacing="4px"
                      sx={{ cursor: "default" }}
                    >
                      <Typography
                        variant="bodySecondaryReg"
                        sx={{ color: "shades.neutral.500" }}
                      >
                        Hidden tags
                      </Typography>
                      <InfoCircleIcon
                        sx={{ width: "12px", color: "shades.neutral.300" }}
                      />
                    </Row>
                  </TooltipWrapper>
                  <TagList>
                    {hiddenAutoTags.map((tag) => (
                      <Tag
                        hidden
                        sizing="padded"
                        key={tag.id}
                        tag={tag}
                        onAction={handleHideTag}
                        actionIcon={<EyeOffIcon />}
                      />
                    ))}
                  </TagList>
                </Column>
              )}
            </TagListWrapper>
          </Row>
        </Setting>
      </SettingsModule>
      <ConfirmationModal
        title="Delete tag?"
        open={tagToConfirmDelete != null}
        onClose={resetTagToDelete}
        onConfirm={handleConfirmDelete}
        size="md"
        confirmProps={{
          type: "negative",
          label: "Delete tag",
        }}
        loading={deleteTag.isPending}
      >
        <Typography sx={{ color: "shades.neutral.600", lineHeight: 20 / 16 }}>
          When you delete your custom tag "
          <Bold>{tagToConfirmDelete?.name}</Bold>", it will be removed from all
          meeting reports that are assigned that tag.
        </Typography>
      </ConfirmationModal>
    </>
  );
};

export default TaggingModule;
